import { useEffect, useState } from "react";
import useSWR from "swr";
import type { AxiosError } from "axios";
import { makeUrlWithParams } from "../../../../util/util";
import type { ListPageState } from "./useListPageState";

export interface ListPageDataConfig<TData, TResponse> {
  endpoint: string;
  requestParams?: { [key: string]: string };
  statusTransformer?: (status: string[]) => string[];
  dataTransformer: (response: TResponse) => TData[];
  paginationTransformer: (response: TResponse) => {
    total: number;
    offset: number;
  };
  getUnreadIds?: (item: TData) => boolean;
  notifications?: { ids: string[] };
}

export interface ListPageData<TData> {
  data: TData[];
  setData: (data: TData[]) => void;
  isLoading: boolean;
  error: AxiosError | undefined;
  mutate: () => void;
}

/**
 * Custom hook that manages data fetching and transformation for list pages
 */
export const useListPageData = <TData, TResponse>(
  state: ListPageState,
  config: ListPageDataConfig<TData, TResponse>
): ListPageData<TData> => {
  const {
    endpoint,
    requestParams,
    statusTransformer,
    dataTransformer,
    paginationTransformer,
    getUnreadIds,
    notifications,
  } = config;

  const {
    offset,
    perPage,
    selectedStatus,
    selectedAccountManagers,
    selectedCustomerServiceReps,
    debouncedSearchQuery,
    setTablePagination,
  } = state;

  const [data, setData] = useState<TData[]>([]);

  const {
    data: response,
    error,
    mutate,
  } = useSWR<TResponse, AxiosError>(
    makeUrlWithParams(endpoint, {
      ...(debouncedSearchQuery && { q: debouncedSearchQuery }),
      status: statusTransformer
        ? statusTransformer(selectedStatus.map((item) => item.value))
        : selectedStatus.map((item) => item.value),
      account_managers: selectedAccountManagers.map((item) => item.value),
      customer_service_reps: selectedCustomerServiceReps.map(
        (item) => item.value
      ),
      offset,
      limit: perPage,
      ...requestParams,
    })
  );

  const isLoading = !response && !error;

  useEffect(() => {
    if (response) {
      const transformedData = dataTransformer(response);
      const pagination = paginationTransformer(response);

      // Apply unread status if notifications are available
      if (getUnreadIds && notifications) {
        const dataWithUnread = transformedData.map((item) => ({
          ...item,
          unread: getUnreadIds(item),
        }));
        setData(dataWithUnread);
      } else {
        setData(transformedData);
      }

      // Update pagination
      setTablePagination({
        perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
        totalCount: pagination.total,
      });
    }
  }, [
    dataTransformer,
    getUnreadIds,
    notifications,
    paginationTransformer,
    perPage,
    response,
    setTablePagination,
  ]);

  return {
    data,
    setData,
    isLoading,
    error,
    mutate,
  };
};
